import { useMelwaysInfo } from '@seek/melways-react';
import { rules } from '@seek/validators-js';
import { useTranslations } from '@vocab/react';
import {
  Box,
  Button,
  FieldMessage,
  HiddenVisually,
  IconDocument,
  IconUpload,
  Inline,
  Loader,
  Stack,
  Text,
  TextLinkButton,
} from 'braid-design-system';
import {
  type ChangeEvent,
  type MouseEvent,
  useEffect,
  useRef,
  useState,
} from 'react';

import type { Resume } from 'src/__generated__/graphql';
import { useTrackingClient } from 'src/context/tracking';
import { DocumentType } from 'src/modules/analytics.types';
import { isIos } from 'src/modules/utils';
import { validate } from 'src/utils/validate';

import type { FileWithID } from '../../Documents';

import translations from './.vocab';

const allowedMediaTypes = [
  'application/msword',
  '.doc',
  '.docx',
  'application/pdf',
  'application/rtf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
];
const resumeRules = rules.resumeUpload({
  fileTypeLabel: 'resumé',
});

interface Props {
  name: string;
  id: string;
  /** @deprecated use reUploadText */
  label?: string;
  /** translated text for "re-upload" text button label */
  reUploadText?: string;
  description: string;
  onChange: (value?: File) => void;
  onClick?: (event: MouseEvent<HTMLInputElement>) => void;
  valid: boolean;
  value: FileWithID;
  documentList?: Resume[];
  message: string;
  isLoading?: boolean;
}

const DocumentUpload = ({
  name,
  id,
  label = '',
  reUploadText,
  description,
  onChange,
  onClick,
  valid,
  value,
  documentList,
  message,
  isLoading,
}: Props) => {
  const { t } = useTranslations(translations);
  const { language } = useMelwaysInfo();
  const fileInputElement = useRef<HTMLInputElement>(null);
  const successElement = useRef<HTMLElement>(null);
  const client = useTrackingClient();
  const documentType = {
    ['resume']: DocumentType.Resume,
    ['coverLetter']: DocumentType.CoverLetter,
    ['selectionCriteria']: DocumentType.SelectionCriteria,
  }[name];
  const [preValidationError, setPreValidationError] = useState('');

  const errorMessage = preValidationError || message;
  const shouldSaveToList = typeof documentList !== 'undefined';

  const showProfileStoreMessage = shouldSaveToList && documentList.length === 0;
  const isSuccessful = Boolean(value) && valid && !Boolean(errorMessage);
  const mode = isSuccessful ? 'success' : 'upload';
  const hasError = Boolean(errorMessage);
  const fileTypeMsgId = `${name}-file-type`;
  const describedBy = hasError ? `${name}-upload-error-message` : fileTypeMsgId;

  const validateAndTrack = async (file: FileWithID): Promise<boolean> => {
    const { type, size }: { type: string; size: number } = file;
    const documentFormat = type.substring(type.lastIndexOf('/') + 1);
    const documentSize: number = size / Math.pow(1024, 2);

    setPreValidationError('');

    const alertShownMessage = await validate({
      ...resumeRules,
      value: file,
      language,
    });

    if (alertShownMessage) {
      client.trackLink({
        link: 'alert_viewed',
        alertShownTone: 'critical',
        alertShownType: 'notice',
        alertShownPosition: 'content',
        alertShownMessage,
        documentType,
        documentFormat,
        documentSize,
      });

      setPreValidationError(alertShownMessage);

      return false;
    }

    return true;
  };

  useEffect(() => {
    if (isSuccessful) {
      const timeout = isIos(navigator.platform) ? 1500 : 0;

      setTimeout(() => {
        if (successElement.current) {
          // Inside block to guarantee ref exists.
          // Since this is called inside a settimeout after the conditions to render the ref
          // on the browser it is not a problem but it can be inside jest
          successElement.current.focus();
        }
      }, timeout);
    }
  }, [isSuccessful]);

  const handleUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const target = event.target;
    const file = (target.files as FileList)[0] as FileWithID;

    if (!file) return;

    const preValidationPassed = await validateAndTrack(file);

    if (!preValidationPassed) return;

    onChange(file);
  };

  const handleBrowse = () => {
    fileInputElement.current?.click();
  };

  const renderErrorMessage = () => (
    <FieldMessage
      {...{
        id: `${name}-upload-error-message`,
        tone: 'critical',
        message: errorMessage,
      }}
    />
  );

  return (
    <>
      <input
        id={`${name}File`}
        data-testid="file-input"
        ref={fileInputElement}
        onClick={onClick}
        onChange={handleUpload}
        type="file"
        accept={allowedMediaTypes.join(',')}
        style={{
          display: 'none',
        }}
      />
      <Stack space="small">
        {mode === 'upload' && (
          <>
            <Inline space="none">
              <Button
                aria-describedby={describedBy}
                tone="formAccent"
                variant="ghost"
                icon={<IconUpload />}
                id={id}
                onClick={handleBrowse}
                data={{ testid: 'upload-button' }}
              >
                {t('Upload')}
              </Button>
            </Inline>
            {!isLoading && !Boolean(errorMessage) && (
              <Text tone="secondary" size="small" id={fileTypeMsgId}>
                {description}
              </Text>
            )}
            {!isLoading && Boolean(errorMessage) && renderErrorMessage()}
          </>
        )}

        {mode === 'success' && (
          <Stack space="medium">
            <Box marginTop="xsmall">
              <Text weight="strong" icon={<IconDocument />}>
                <Box
                  component="span"
                  tabIndex={-1}
                  ref={successElement}
                  data={{ testid: 'upload-success-filename' }}
                  data-hj-suppress
                >
                  {value?.name}
                </Box>
              </Text>
            </Box>
            {!isLoading && (
              <Text>
                <TextLinkButton
                  id={`${name}Field`}
                  aria-describedby={describedBy}
                  onClick={handleBrowse}
                >
                  {reUploadText
                    ? reUploadText
                    : t('Upload a different', { label })}
                </TextLinkButton>
              </Text>
            )}
            {!isLoading && Boolean(errorMessage) && renderErrorMessage()}
          </Stack>
        )}

        {isLoading && (
          <Box paddingY={showProfileStoreMessage ? 'small' : 'none'}>
            <Box role="status">
              <HiddenVisually>Loading</HiddenVisually>
            </Box>
            <Loader size="xsmall" />
          </Box>
        )}
      </Stack>
    </>
  );
};

export default DocumentUpload;
